import { useState } from "react";
import type { ColumnDef, SortingState } from "@tanstack/react-table";
// ADV-1176 / ADR-0048: /service-plans is the plan-management register — the
// reusable service/maintenance plan templates (membership types) that get
// stamped onto customer agreements. Plans are account-scoped configuration.
import { useServicePlans } from "@/hooks/use-api";
import { ListPage } from "@/components/v2/list-page";
import { DataTable } from "@/components/v2/data-table";
import { StatusBadge } from "@/components/status-badge";
import { formatCurrency } from "@/lib/utils";

type Row = Record<string, unknown>;

function titleCase(value: string): string {
  return value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function termLabel(
  value: number | undefined,
  unit: string | undefined,
): string {
  if (value == null || !unit) return "—";
  const singular = value === 1 ? unit.replace(/s$/, "") : unit;
  return `${value} ${singular}`;
}

const columns: ColumnDef<Row, unknown>[] = [
  {
    accessorKey: "name",
    header: "Plan",
    enableSorting: false,
    cell: ({ row }) => {
      const name = row.original.name as string | undefined;
      const description = row.original.description as string | undefined;
      return (
        <span className="flex flex-col">
          <span className="typo-body font-medium text-[color:var(--color-text-hi)]">
            {name || "—"}
          </span>
          {description && (
            <span className="typo-label text-[color:var(--color-text-lo)] truncate max-w-[28ch]">
              {description}
            </span>
          )}
        </span>
      );
    },
  },
  {
    accessorKey: "billing_amount",
    header: "Billing",
    cell: ({ row }) => {
      const amount = row.original.billing_amount as number | undefined;
      const frequency = row.original.billing_frequency as string | undefined;
      return (
        <span className="flex flex-col">
          <span className="typo-mono text-[color:var(--color-text-hi)]">
            {amount != null ? formatCurrency(amount) : "—"}
          </span>
          {frequency && (
            <span className="typo-label text-[color:var(--color-text-lo)]">
              {titleCase(frequency)}
            </span>
          )}
        </span>
      );
    },
  },
  {
    accessorKey: "term_length_value",
    header: "Term",
    enableSorting: false,
    cell: ({ row }) => {
      const value = row.original.term_length_value as number | undefined;
      const unit = row.original.term_length_unit as string | undefined;
      return (
        <span className="text-[color:var(--color-text-md)]">
          {termLabel(value, unit)}
        </span>
      );
    },
  },
  {
    accessorKey: "visit_cadence",
    header: "Visits / Term",
    enableSorting: false,
    cell: ({ row }) => {
      const value = row.original.visit_cadence as number | null | undefined;
      return (
        <span className="text-[color:var(--color-text-md)]">
          {value != null ? value : "—"}
        </span>
      );
    },
  },
  {
    accessorKey: "is_active",
    header: "Status",
    enableSorting: false,
    cell: ({ row }) => {
      const active = row.original.is_active as boolean | undefined;
      return <StatusBadge status={active ? "active" : "inactive"} />;
    },
  },
];

export default function V2ServicePlansPage() {
  const [sorting, setSorting] = useState<SortingState>([
    { id: "billing_amount", desc: true },
  ]);
  const [pageIndex, setPageIndex] = useState(0);
  const pageSize = 50;

  const { data, isLoading } = useServicePlans({
    offset: pageIndex * pageSize,
    limit: pageSize,
    sort_by: sorting[0]?.id,
    sort_order: sorting[0]?.desc ? "desc" : "asc",
  });

  const plans = (data?.service_plans ?? []) as Row[];
  const total = data?.total ?? plans.length;

  return (
    <ListPage
      title="Service Plans"
      subtitle={`${total.toLocaleString()} plan templates`}
    >
      <DataTable
        columns={columns}
        data={plans}
        total={total}
        isLoading={isLoading}
        emptyMessage="No service plans yet"
        emptyDescription="Plans are the reusable membership templates — billing cadence, visit cadence, and term — that get sold on a proposal and stamped onto customer agreements."
        pageSize={pageSize}
        pageIndex={pageIndex}
        onPaginationChange={setPageIndex}
        sorting={sorting}
        onSortingChange={setSorting}
      />
    </ListPage>
  );
}
